import { MarkerClusterer } from "@googlemaps/markerclusterer";

// #region StateStore
export class StateStore {

    /**
     * Purpose:
     * Remembering the state of the map and filters during session.
     * 
     * Example:
     * An user had picked some options in the left-side filters, moved on the map and zoomed it in.
     * After he clicked on a profile page link, the state of the RDAs/DCs page was serialized into sessionStorage.
     * When he will click on the "Go back" button in his browser, the state of the RDAs/DCs page will be restored.
     * And he will be able to seamlessly continue where he had finished.
     * 
     * 
     * TODO:
     * The StateStore uses sessionStorage.
     * We assume, that the user opens only single tab with RDAs/DCs map page.
     * It is okay, if hte user opens one tab with RDAs page and on tab with DCs page.
     * There might be an issue if the user opens multiple tabs with e.g. RDAs map pages.
     * If there are multiple tabs with the same map page, those maps will have the same ID ~ key in sessionStorage.
     * It may result in incosistent updates of the state.
     * The solution is to use SharedWorker for synchronizing the state.
     * https://developer.mozilla.org/en-US/docs/Web/API/SharedWorker
     */

    // #region Properties
    defaultState;
    currentState;
    interactiveMap;
    // #endregion

    // #region Constructor
    constructor(interactiveMap, defaultState) {
        this.defaultState = defaultState;
        this.interactiveMap = interactiveMap;
        window.addEventListener("beforeunload", () => this.pageToState());
    }
    // #endregion

    // #region Get/Update state
    getState() {
        if (!this.currentState) {
            let stateFromStorage = JSON.parse(sessionStorage[this.interactiveMap.mapUid] ?? "false");
            this.currentState = stateFromStorage ? stateFromStorage : this.defaultState;
        }
        return this.currentState;
    }
    updateState(updateFunction) {
        if (typeof (updateFunction) === "function") {
            updateFunction(this.currentState);
            sessionStorage[this.interactiveMap.mapUid] = JSON.stringify(this.currentState);
        }
    }
    // #endregion

    // #region State <-> Page
    stateToPage() {
        const state = this.getState();

        this.interactiveMap.toggleListView(state.listMode);

        // #region Inputs
        const inputsState = state.inputs ?? {};
        $("input").each((i, el) => {
            const $el = $(el);
            const value = inputsState[$el.attr("id")];
            switch ($el.attr("type")) {
                case "radio":
                case "checkbox":
                    $el.prop("checked", value);
                    break;
                case "text":
                case "number":
                case "range":
                case "hidden":
                default:
                    $el.val(value);
                    break;
            }
        });
        // #endregion

        // #region Accordions
        const accordionsState = state.accordions ?? {};
        for (let id in accordionsState) {
            let $el = $("#" + id);
            let show = accordionsState[id];
            $el.toggleClass("show", show);
            $el.parent().find(".accordion-button").toggleClass("collapsed", !show);
        }
        // #endregion

        // #region Map
        this.interactiveMap.map.setZoom(state.zoom);
        this.interactiveMap.map.setCenter(state.center);
        // #endregion
    }
    pageToState() {
        this.updateState(state => {

            state.listMode = this.interactiveMap.listMode ?? false;

            // #region Inputs
            state.inputs = {};
            let center = this.interactiveMap.map.getCenter();
            $("input").each((i, el) => {
                const $el = $(el);
                let value;
                switch ($el.attr("type")) {
                    case "radio":
                    case "checkbox":
                        value = $el.is(":checked");
                        break;
                    case "text":
                    case "number":
                    case "range":
                    case "hidden":
                    default:
                        value = $el.val();
                        break;
                }
                state.inputs[$el.attr("id")] = value;
            });
            // #endregion

            // #region Accordions
            state.accordions = {};
            $(".accordion-collapse").each((i, el) => {
                const $el = $(el);
                state.accordions[$el.attr("id")] = $el.hasClass("show");
            });
            // #endregion

            // #region Map
            state.center = { lat: center.lat(), lng: center.lng() };
            state.zoom = this.interactiveMap.map.getZoom();
            state.infoWindows = {};
            for (let id in this.interactiveMap.allItems) {
                state.infoWindows[id] = this.interactiveMap.allItems[id].infoWindow.getMap() != null;
            }
            // #endregion
        });
    }
    // #endregion
}
// #endregion

// #region InteractiveMap
export class InteractiveMap {

    // #region Static
    // Handling possible multiple map instances on 1 page
    static instances = {};
    // #endregion

    // #region Properties
    options;
    map;
    mapUid;
    list;
    listMode = false;
    stateStore;
    toggleListView = () => { };
    allItems = {};
    templates = {};
    // #endregion

    // #region Constructor
    constructor(options) {
        this.options = options;
        if (!this.options.maxZoom) {
            this.options.maxZoom = 18;
        }
        this.templates = options.templates(this);
        // Key for Google Maps initilaization and sessionStorage
        this.mapUid = "InteractiveMap_URL" + location.pathname.replace(/[\.*-\/\\]/gmi, "_") + "_ID_" + this.options.mapElementId.replace(/[\.*-\/\\]/gmi, "_");
        InteractiveMap.instances[this.mapUid] = this;
        this.list = $("#" + this.options.listElementId);
        this.loadGoogleMaps();
        this.stateStore = new StateStore(this, {
            listMode: this.listMode,
            center: this.options.center,
            zoom: this.options.zoom,
            inputs: {},
            accordions: {},
            infoWindows: {}
        });
    }
    // #endregion

    // #region Loading Google Maps
    loadGoogleMaps() {
        // Add Google Maps script only if neccessary
        if (window.google?.maps) {
            this.init();
        } else {
            let that = this;
            window['init' + this.mapUid] = () => this.init();
            let src = "https://maps.googleapis.com/maps/api/js?key=" + (this.options.apiKey) + "&loading=async" + "&callback=window.init" + this.mapUid + "&libraries=marker";
            let script = document.createElement("script");
            script.type = 'text/javascript';
            script.async = true;
            script.defer = true;
            script.src = src;
            document.body.appendChild(script);
        }
    }
    // #endregion

    // #region Initialization
    init() {
        this.createMap();
        this.createZoomControls();
        this.initTabSwitch();
        this.stateStore.stateToPage();
        this.withData(this.options.onLoad);
    }
    // #endregion

    // #region createMap
    createMap() {
        const map = new google.maps.Map(document.getElementById(this.options.mapElementId ?? "map"), {
            scrollwheel: false,
            disableDefaultUI: true,
            mapTypeControl: false,
            // Google cloud map id not map element id.
            // mapId is needed in order for the new google.maps.marker.AdvancedMarkerElement to work.
            // Default to google demo map id if mapId is not specified in order for the markers to work. 
            // The map itself might have different styles.
            mapId: this.options.mapId ?? "DEMO_MAP_ID"
        });
        // Limiting max-zoom
        map.addListener("zoom_changed", () => {
            if (map.getZoom() > this.options.maxZoom) {
                map.setZoom(this.options.maxZoom);
            } 
        });
        this.map = map;
        return map;
    }
    resetMap() {
        this.map.setCenter(this.options.center);
        this.map.setZoom(this.options.zoom);
    }
    // #endregion

    // #region ZoomControls
    createZoomControls() {
        let map = this.map;
        let zoomControlDiv = document.createElement('div');
        zoomControlDiv.setAttribute('class', 'custom-controls-wrapper');

        // Wrapper for custom zoom buttons
        let controlWrapper = document.createElement('div');
        controlWrapper.setAttribute('class', 'custom-zoom-wrapper');
        zoomControlDiv.appendChild(controlWrapper);

        // zoomOut
        let zoomOutButton = document.createElement('button');
        zoomOutButton.setAttribute('class', 'custom-zoom-out');

        // zoomIn
        let zoomInButton = document.createElement('button');
        zoomInButton.setAttribute('class', 'custom-zoom-in');
        controlWrapper.appendChild(zoomInButton);
        controlWrapper.appendChild(zoomOutButton);

        // Setup the click event listener - zoomOut
        zoomOutButton.addEventListener('click', function () {
            map.setZoom(map.getZoom() - 1);
        });

        // Setup the click event listener - zoomIn
        zoomInButton.addEventListener('click', function () {
            map.setZoom(map.getZoom() + 1);
        });

        zoomControlDiv.index = 1;
        this.map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(zoomControlDiv);
    }
    // #endregion

    // #region Backend request
    withData(callback) {
        this.options.dataRequest.success = (data) => {
            if (data != undefined) {
                callback.apply(this, [data]);
            }
        };
        return $.ajax(this.options.dataRequest);
    }
    // #endregion

    // #region Clustering
    createCluster(markers) {
        let clusterer = new MarkerClusterer({
            map: this.map,
            markers: markers,
            algorithmOptions: { maxZoom: (this.options.maxZoom + 1) }
        });
        clusterer.onClusterClick = ((proxied) => {
            return (event, cluster, map) => {
                if (this.map.getZoom() < this.options.maxZoom)
                {
                    // Call to the original onClusterClick function
                    proxied(event, cluster, map);
                }
                else
                {
                    // The zoom is maximal, but there is still a cluster = multiple items in the same place
                    let content = cluster.markers.map((m) => {
                        return this.templates.infoWindow(m.item);
                    }).join('<br/>');
                    const infoWindow = new google.maps.InfoWindow();
                    infoWindow.open(map, cluster.markers[0]);
                    infoWindow.setContent(content);
                }
            }
        })(clusterer.onClusterClick);
        markers.forEach(marker => marker.cluster = clusterer);
        return clusterer;
    }
    // #endregion

    // #region Markers
    createMarker(item) {
        const map = this.map;
        const id = this.templates.id(item);
        const icon = this.templates.icon(item);
        const title = this.templates.title(item);
        const pinImg = document.createElement("img");
        pinImg.src = icon.url;
        pinImg.height = icon.scaledSize.height;
        pinImg.width = icon.scaledSize.width;
        const marker = new google.maps.marker.AdvancedMarkerElement({
            map: map,
            position: item.coordinates ?? { lat: 0, lng: 0 },
            title: title,
            content: pinImg,
            gmpClickable: true
        });
        marker.markerVisible = true;
        marker.item = item;

        const infoWindow = new google.maps.InfoWindow();
        const openWindow = () => {
            infoWindow.open(map, marker);
            infoWindow.setContent(this.templates.infoWindow(item));
        }
        marker.addListener('click', openWindow);
        if ((this.stateStore.getState().infoWindows ?? {})[id]) {
            openWindow();
        }

        this.createListItem(item);

        item.marker = marker;
        item.infoWindow = infoWindow;

        this.allItems[id] = item;
        return marker;
    }
    createMarkers(items) {
        let markers = [];
        items.forEach(item => {
            markers.push(this.createMarker(item));
        });     
        return markers;
    }
    toggleMarker(item, show) {
        let oldState = item.marker.markerVisible;
        let newState = show != undefined ? show : !item.marker.markerVisible;
        if (newState != oldState) {
            item.marker.markerVisible = newState;
            if (!newState) {
                // If the marker is not visible, then the infowindow should be closed
                item.infoWindow.close();
            }
            newState ? item.marker.cluster.addMarker(item.marker, false) : item.marker.cluster.removeMarker(item.marker, false);
        }
    }
    toggleMarkers(items, isVisible) {
        items.forEach(item => this.toggleMarker(item, isVisible));
    }
    // #endregion

    // #region List
    createListItem(item) {
        item.listItem = $(this.templates.listItem(item));
        list.append(item.listItem[0]);
    }
    toggleListItem(item, show) {
        item.listItem.toggle(show);
    }
    // #endregion

    // #region Map / List switching
    toggleItem(item, isVisible) {
        this.toggleListItem(item, isVisible);
        this.toggleMarker(item, isVisible);
    }
    initTabSwitch() {
        const that = this;
        const mapContainer = $("#" + this.options.mapElementId);
        const listContainer = $("#" + this.options.listContainerId);
        const mapBtn = $("#" + this.options.mapBtnId);
        const listBtn = $("#" + this.options.listBtnId);

        this.toggleListView = (listMode) => {
            that.listMode = listMode;
            mapContainer.toggle(!listMode);
            mapBtn.toggleClass("button-active", !listMode);
            listContainer.toggle(listMode);
            listBtn.toggleClass("button-active", listMode);
            if (typeof (that.options.onTabSwitch) === "function") {
                that.options.onTabSwitch();
            }
        }

        listBtn.on('click', () => that.toggleListView(true));
        mapBtn.on('click', () => that.toggleListView(false));
    }
    // #endregion

    // #region KML Layers
    showKmlLayer(kmlLayer) {
        if (!kmlLayer.layer) {
            if (kmlLayer.kmlUrl.length) {
                kmlLayer.layer = new google.maps.KmlLayer({
                    url: kmlLayer.kmlUrl,
                    preserveViewport: true,
                    map: this.map
                });
            }
        } else {
            kmlLayer.layer.setMap(this.map);
        }
    }

    hideKmlLayer(layer) {
        if (layer.layer) {
            layer.layer.setMap(null);
        }
    }
    // #endregion
}
// #endregion